import { toast } from "sonner";
import { Download, Cpu, User, ShieldCheck, KeyRound, Timer as TimerIcon, Award } from "lucide-react";
import { auditLog as fixtureAudit } from "@/data/fixtures";
import { usePersona } from "@/context/PersonaContext";

// Colored badges for the "kind" of an audit entry. Only entries whose
// kind matches one of these render a badge; regular entries stay quiet.
const kindStyles = {
  grant: {
    label: "GRANT",
    color: "var(--mos-green)",
    bg: "var(--mos-green-bg)",
    border: "var(--mos-green-border)",
    icon: Award,
  },
  revoke: {
    label: "REVOKE",
    color: "var(--mos-amber)",
    bg: "var(--mos-amber-bg)",
    border: "var(--mos-amber-border)",
    icon: Award,
  },
  signature: {
    label: "SIGNATURE",
    color: "var(--mos-accent)",
    bg: "#EFF3F9",
    border: "#C9D6E6",
    icon: ShieldCheck,
  },
  session: {
    label: "SESSION",
    color: "#475569",
    bg: "#F1F5F9",
    border: "#CBD5E1",
    icon: TimerIcon,
  },
};

function KindBadge({ kind }) {
  const s = kindStyles[kind];
  if (!s) return null;
  const Icon = s.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border align-middle mr-2"
      style={{ color: s.color, background: s.bg, borderColor: s.border }}
    >
      <Icon size={9} />
      {s.label}
    </span>
  );
}

export default function AuditLog() {
  const { dynamicAudit } = usePersona();

  const onExport = () => {
    toast.success("Audit log exported · CSV", { duration: 4000 });
  };

  // Merge dynamic (runtime-generated) events at the top, then fixture.
  // String timestamps in "May DD HH:MM" format sort lexicographically
  // in the range we care about.
  const rows = [...dynamicAudit, ...fixtureAudit].sort((a, b) =>
    a.timestamp < b.timestamp ? 1 : -1
  );

  return (
    <div data-testid="page-audit-log" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">
            Audit Log
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            Immutable event trail
          </h1>
          <p className="mt-2 text-[13.5px] text-slate-600 max-w-xl">
            Every AI output, human decision, exported action, and authority
            grant. Newest first. One source of truth for compliance and
            post-mortem review.
          </p>
        </div>
        <button
          type="button"
          onClick={onExport}
          className="mos-btn mos-btn-secondary"
          data-testid="btn-export-audit"
        >
          <Download size={14} />
          Export
        </button>
      </div>

      {/* Legend · quiet, sits above the table */}
      <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
        <span className="font-semibold uppercase tracking-wider">Kinds</span>
        {Object.entries(kindStyles).map(([k, s]) => (
          <span
            key={k}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border"
            style={{ color: s.color, background: s.bg, borderColor: s.border }}
          >
            <s.icon size={9} />
            <span className="font-semibold tracking-wider">{s.label}</span>
          </span>
        ))}
      </div>

      <div
        className="mos-surface rounded-md overflow-hidden"
        data-testid="audit-table"
      >
        <table className="w-full text-left">
          <thead>
            <tr
              className="text-[11px] uppercase tracking-wider text-slate-500"
              style={{ background: "#FAFBFC" }}
            >
              <th className="py-3 pl-6 pr-4 font-semibold w-[160px]">
                Timestamp
              </th>
              <th className="py-3 px-4 font-semibold w-[220px]">Actor</th>
              <th className="py-3 px-4 font-semibold">Action</th>
              <th className="py-3 px-6 font-semibold w-[340px]">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id}
                className="border-t align-top"
                style={{
                  borderColor: "var(--mos-border)",
                  background: i % 2 === 1 ? "#FBFBFC" : "transparent",
                }}
                data-testid={`audit-row-${row.id}`}
              >
                <td className="py-4 pl-6 pr-4 mos-mono text-[13px] text-slate-800 whitespace-nowrap">
                  {row.timestamp}
                </td>
                <td className="py-4 px-4">
                  <span className="inline-flex items-center gap-2 text-[13px]">
                    <span
                      className="h-6 w-6 rounded-full flex items-center justify-center flex-none"
                      style={{
                        background: row.isSystem ? "#EEF2FF" : "#F1F5F9",
                      }}
                    >
                      {row.isSystem ? (
                        <Cpu size={12} style={{ color: "var(--mos-accent)" }} />
                      ) : (
                        <User size={12} className="text-slate-600" />
                      )}
                    </span>
                    <span
                      className={
                        row.isSystem
                          ? "mos-mono font-medium text-slate-800"
                          : "text-slate-900"
                      }
                    >
                      {row.actor}
                    </span>
                  </span>
                </td>
                <td className="py-4 px-4 text-[13.5px] text-slate-900">
                  <KindBadge kind={row.kind} />
                  {row.action}
                </td>
                <td className="py-4 px-6 text-[13px] text-slate-600">
                  {row.detail}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11.5px] text-slate-500">
        {rows.length} entries · Newest first · Grants, signatures, and session
        events participate in the same immutable trail.
      </p>
    </div>
  );
}
